import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper.js';

export function useHouses(params?: { search?: string }) {
  return useQuery({
    queryKey: ['houses', params],
    queryFn: async () => {
      const res = await api.get('/houses', { params });
      return res.data;
    },
  });
}

export function useHouse(id: string | number | undefined) {
  return useQuery({
    queryKey: ['house', id],
    queryFn: async () => {
      const res = await api.get(`/houses/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateHouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await api.post('/houses', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      toast.success('House created successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useUpdateHouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number | string; name: string; description?: string }) => {
      const res = await api.put(`/houses/${id}`, data);
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      queryClient.invalidateQueries({ queryKey: ['house', data?.data?.id] });
      toast.success('House updated successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useExportHouses() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get('/houses/export-all', { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useExportHouseDetail() {
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.get(`/houses/${id}/export-detail`, { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useDownloadHouseTemplate() {
  return useMutation({
    mutationFn: async () => {
      const res = await api.get('/houses/template', { responseType: 'blob' });
      return res.data;
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useImportHouses() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/houses/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      toast.success(data?.message || 'Import complete');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}

export function useDeleteHouse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number | string) => {
      const res = await api.delete(`/houses/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['houses'] });
      toast.success('House deleted successfully');
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
}
